import Cookies from 'js-cookie';
import { Controller } from 'stimulus';
import events from 'lib/events';
import { triggerCustomEvent } from 'lib/helpers';

export default class SearchFormController extends Controller {
  static targets = ['form', 'input', 'submit', 'autocompleteSuggestions'];

  navigationKeyCodes = [37, 38, 39, 40, 13];

  exclusionKeyCodes = [91, 17, 21, 18, 93];

  connect() {
    this.autocompleteUrl = this.inputTarget.dataset.autocompleteSource;

    // If not mobile/auto-focus search input & add autocomplete listener
    // --------------------------------------
    if (!this.isMobile()) {
      const searchVal = this.inputTarget.value;
      this.inputTarget.value = '';
      this.inputTarget.focus();
      this.inputTarget.value = searchVal;
    }

    // Add is-ready class to search field, so we can apply styles after focused
    // (if we ever remove auto-focus, we need to change/re-think this)
    this.inputTarget.classList.add('is-ready');

    if (this.resultsCount === '0') {
      triggerCustomEvent(events.search.without_results, { searchTerm: this.searchTerm });
    }

    if (this.pageNumber === '1' && this.searchType) {
      triggerCustomEvent(events.search.search_type, { searchType: this.searchType, searchTerm: this.searchTerm });
    }
  }

  handleInputKeyUp = (event) => {
    if (this.navigationKeyCodes.includes(event.keyCode) && document.querySelector('.ez-search-form__suggestions li')) {
      this.navigateSuggestions(event.keyCode);
    } else if (!this.exclusionKeyCodes.includes(event.keyCode) && this.inputTarget.value.length >= 2) {
      this.queryAutocomplete(this.inputTarget.value);
    } else if (this.autocompleteSuggestionsTarget.innerHTML !== '') {
      this.resetAutocomplete();
    }
  }

  handleSuggestionClick = (event) => {
    this.inputTarget.value = event.target.textContent;
    this.submit(event);
  }

  navigateSuggestions = (keyCode) => {
    const suggestionsList = document.querySelector('.ez-search-form__suggestions');
    const activeSuggestion = suggestionsList.querySelector('li.is-active');

    const navUp = () => {
      if (activeSuggestion && activeSuggestion.previousSibling) {
        activeSuggestion.classList.remove('is-active');
        activeSuggestion.previousSibling.classList.add('is-active');
      } else if (activeSuggestion && !activeSuggestion.previousSibling) {
        activeSuggestion.classList.remove('is-active');
        suggestionsList.querySelector('li:last-of-type').classList.add('is-active');
      } else {
        suggestionsList.querySelector('li:last-of-type').classList.add('is-active');
      }
      this.inputTarget.value = suggestionsList.querySelector('li.is-active').textContent;
    };

    const navDown = () => {
      if (activeSuggestion && activeSuggestion.nextSibling) {
        activeSuggestion.classList.remove('is-active');
        activeSuggestion.nextSibling.classList.add('is-active');
      } else if (activeSuggestion && !activeSuggestion.nextSibling) {
        activeSuggestion.classList.remove('is-active');
        suggestionsList.querySelector('li:first-of-type').classList.add('is-active');
      } else {
        suggestionsList.querySelector('li:first-of-type').classList.add('is-active');
      }
      this.inputTarget.value = suggestionsList.querySelector('li.is-active').textContent;
    };

    if (keyCode === 38) {
      navUp();
    } else if (keyCode === 40) {
      navDown();
    } else if (keyCode === 13) {
      this.resetAutocomplete();
    }
  }

  resetAutocomplete = () => {
    this.data.set('lastTerm', '');
    this.autocompleteSuggestionsTarget.innerHTML = '';
    this.formTarget.classList.remove('is-suggesting');
  }

  renderAutocomplete(res, req) {
    this.autocompleteSuggestionsTarget.innerHTML = '';
    this.formTarget.classList.add('is-suggesting');

    if (!res.length > 0) {
      this.resetAutocomplete();
      return;
    }

    Object.keys(res).forEach((i) => {
      const node = document.createElement('LI');

      if (res[i].indexOf(req) >= 0) {
        node.innerHTML = res[i].replace(req, `<strong>${req}</strong>`);
      } else {
        node.innerHTML = res[i];
      }

      node.dataset.action = 'mousedown->search-form#handleSuggestionClick';
      this.autocompleteSuggestionsTarget.appendChild(node);
    });
  }

  queryAutocomplete = (req) => {
    if (req === this.data.get('lastTerm')) return;
    this.data.set('lastTerm', this.inputTarget.value);

    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${this.autocompleteUrl}?term=${encodeURI(req)}`, true);
    xhr.setRequestHeader('Content-Type', 'text/plain');

    xhr.onload = () => {
      this.renderAutocomplete(JSON.parse(xhr.response), req);
    };

    xhr.send();
  }

  isMobile() {
    return document.body.classList.contains('is-mobile');
  }

  submit(event) {
    event.preventDefault();
    Cookies.set('ezSearchType', 'internal');
    triggerCustomEvent('Searched', {
      term: this.inputTarget.value,
      searchRoute: this.inputTarget.getAttribute('data-search-route'),
    });
  }

  get resultsCount() {
    const searchInfo = document.querySelector('[data-search-results-count]');
    if (searchInfo) {
      return searchInfo.getAttribute('data-search-results-count');
    }

    return null;
  }

  get pageNumber() {
    const queryString = window.location.search.slice(1);
    const params = new URLSearchParams(queryString);

    if (params.has('page')) {
      return params.get('page');
    }

    return '1';
  }

  get searchTerm() {
    return this.element.getAttribute('data-search-term').replace(/ /g, '-');
  }

  get searchType() {
    return this.element.getAttribute('data-search-type');
  }
}
